import styled, { keyframes } from "styled-components";

const backdropIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const imageIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const GalleryDialog = styled.div`
  position: fixed;
  z-index: 10000;
  inset: 0;
  display: grid;
  grid-template-columns: minmax(52px, 8vw) minmax(0, 1fr) minmax(52px, 8vw);
  grid-template-rows: 72px minmax(0, 1fr) 44px;
  align-items: center;
  justify-items: center;
  background-color: rgba(15, 17, 21, 0.92);
  animation: ${backdropIn} 0.2s ease both;

  @media (max-width: 520px) {
    grid-template-columns: 48px minmax(0, 1fr) 48px;
    grid-template-rows: 60px minmax(0, 1fr) 38px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Toolbar = styled.div`
  grid-column: 1 / -1;
  grid-row: 1;
  z-index: 2;
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
  justify-self: end;
  align-self: start;
  padding: ${({ theme }) => theme.padding.small};
`;

export const IconButton = styled.button`
  display: inline-flex;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  background-color: rgba(242, 245, 248, 0.14);
  color: ${({ theme }) => theme.colors.offWhite};
  cursor: pointer;
  transition: background-color 0.25s ease, box-shadow 0.25s ease,
    opacity 0.25s ease;

  i {
    font-size: ${({ theme }) => theme.typography.body.fontSize};
  }

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.accent};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 3px;
  }

  &:active:not(:disabled) {
    box-shadow: ${({ theme }) => theme.shadow.buttonActive};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  @media (max-width: 520px) {
    width: 38px;
    height: 38px;

    i {
      font-size: ${({ theme }) => theme.typography.medium.fontSize};
    }
  }
`;

export const NavigationButton = styled(IconButton)<{
  $side: "left" | "right";
}>`
  grid-column: ${({ $side }) => ($side === "left" ? 1 : 3)};
  grid-row: 2;
  width: 52px;
  height: 52px;

  @media (max-width: 520px) {
    width: 38px;
    height: 38px;
  }
`;

export const GalleryViewport = styled.div`
  grid-column: 2;
  grid-row: 2;
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
`;

export const GalleryCanvas = styled.div<{ $scale: number }>`
  display: flex;
  flex: 0 0 auto;
  width: ${({ $scale }) => `${Math.max(1, $scale) * 100}%`};
  height: ${({ $scale }) => `${Math.max(1, $scale) * 100}%`};
  align-items: center;
  justify-content: center;
`;

export const GalleryImage = styled.img<{ $scale: number }>`
  display: block;
  max-width: ${({ $scale }) => `${100 / Math.max(1, $scale)}%`};
  max-height: ${({ $scale }) => `${100 / Math.max(1, $scale)}%`};
  object-fit: contain;
  user-select: none;
  transform: scale(${({ $scale }) => $scale});
  transform-origin: center;
  animation: ${imageIn} 0.25s ease both;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    animation: none;
  }
`;

export const GalleryCounter = styled.div`
  grid-column: 2;
  grid-row: 3;
  padding: ${({ theme }) => theme.padding.xsmall}
    ${({ theme }) => theme.padding.small};
  border-radius: ${({ theme }) => theme.borderRadius.regular};
  background-color: rgba(242, 245, 248, 0.14);
  color: ${({ theme }) => theme.colors.offWhite};
  font-size: ${({ theme }) => theme.typography.medium.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;
